import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  LinearProgress,
  IconButton,
  Box,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  Autorenew as RegenerateIcon,
  PlayArrow as RetryIcon,
  Star as ActiveIcon,
  StarBorder as InactiveIcon
} from '@mui/icons-material';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { PLAN_STATUS, setActiveReport } from '../utils/careerReportService';

/**
 * CareerReportCard Component
 * Displays a career level-up report with actions (view, download, delete, regenerate)
 */
const CareerReportCard = ({
  report,
  platformName = 'MindTrace',
  onRefresh,
  onDelete,
  onRegenerate,
  selected = false,
  onSelect,
  showCheckbox = false
}) => {
  const [retrying, setRetrying] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [settingActive, setSettingActive] = useState(false);

  const getPlanStatusBadge = () => {
    const planStatus = report.planStatus || (report.isActive ? PLAN_STATUS.ACTIVE : PLAN_STATUS.DRAFT);
    
    switch (planStatus) {
      case PLAN_STATUS.ACTIVE:
        return { label: 'ACTIVE', color: '#22c55e', bgcolor: 'rgba(34, 197, 94, 0.15)' };
      case PLAN_STATUS.COMPLETED:
        return { label: 'COMPLETED', color: '#fbbf24', bgcolor: 'rgba(251, 191, 36, 0.15)' };
      case PLAN_STATUS.PARTIAL:
        return { label: 'PAUSED', color: '#3b82f6', bgcolor: 'rgba(59, 130, 246, 0.15)' };
      case PLAN_STATUS.DRAFT:
      default:
        return { label: 'DRAFT', color: '#737373', bgcolor: 'rgba(115, 115, 115, 0.15)' };
    }
  };

  const handleSetActive = async () => {
    try {
      setSettingActive(true);
      await setActiveReport(report.id, report.userId);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Failed to set active:', error);
      alert('Failed to set as active plan');
    } finally {
      setSettingActive(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
      case 'analyzed':
        return 'success';
      case 'processing':
      case 'analyzing':
      case 'payment_complete':
        return 'warning';
      case 'failed':
        return 'error';
      case 'ready_for_payment':
      case 'payment_pending':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusText = () => {
    const status = report.status;
    
    switch (status) {
      case 'completed':
        return report.pdfUrl ? 'Ready' : 'Generating PDF...';
      case 'analyzed':
        return report.pdfUrl ? 'Ready' : 'Finalizing Report...';
      case 'processing':
      case 'analyzing':
        return 'Building Your Plan...';
      case 'payment_complete':
        return 'Getting Started...';
      case 'failed':
        return 'Failed';
      case 'ready_for_payment':
      case 'payment_pending':
        return 'Pending Payment';
      default:
        return status;
    }
  };

  const handleViewReport = () => {
    if (report.pdfUrl) {
      window.open(report.pdfUrl, '_blank');
    } else {
      alert('PDF generation in progress. Please check back soon.');
    }
  };

  const handleDownloadReport = () => {
    if (report.pdfUrl) {
      const link = document.createElement('a');
      link.href = report.pdfUrl;
      link.download = `${platformName}-Career-Level-Up-${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
    }
  };

  const handleRetryAnalysis = async () => {
    try {
      setRetrying(true);
      console.log('🔄 Manually triggering career analysis for report:', report.id);
      
      const functions = getFunctions();
      // Extended timeout for Grok web search (5 minutes)
      const analyzeCareerPath = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 });
      
      const result = await analyzeCareerPath({ reportId: report.id });
      
      console.log('✅ Career analysis triggered successfully:', result.data);
      alert('✅ Analysis started! Your career report will be ready in 3-5 minutes.');
      
      // Reload reports after a short delay
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 2000);
      }
      
    } catch (error) {
      console.error('❌ Career analysis call failed:', error);
      alert(`❌ Analysis failed: ${error.message}\n\nError code: ${error.code}\n\nPlease check:\n1. Admin → AI Agents → Career Analyzer enabled\n2. Admin → AI Agents → API keys configured\n3. Admin → Settings → RapidAPI key configured\n4. Browser console for detailed logs`);
    } finally {
      setRetrying(false);
    }
  };

  const handleRegenerateReport = async () => {
    if (!window.confirm('Regenerate this career report? This will create a new analysis using the same resume data.')) {
      return;
    }

    try {
      setRegenerating(true);
      console.log('🔄 Regenerating career report:', report.id);
      
      // Mark as regenerated in Firestore
      const { getFirestore, doc, updateDoc } = await import('firebase/firestore');
      const db = getFirestore();
      await updateDoc(doc(db, 'careerReports', report.id), {
        regenerated: true,
        regeneratedAt: new Date().toISOString()
      });
      
      const functions = getFunctions();
      // Extended timeout for Grok web search (5 minutes)
      const analyzeCareerPath = httpsCallable(functions, 'analyzeCareerPath', { timeout: 300000 });
      
      const result = await analyzeCareerPath({ reportId: report.id });
      
      console.log('✅ Report regeneration triggered:', result.data);
      alert('✅ Report regeneration started! Check back in 3-5 minutes.');
      
      if (onRegenerate) {
        onRegenerate(report.id);
      }
      
      // Reload after delay
      if (onRefresh) {
        setTimeout(() => {
          onRefresh();
        }, 2000);
      }
      
    } catch (error) {
      console.error('❌ Regeneration failed:', error);
      alert(`❌ Regeneration failed: ${error.message}`);
    } finally {
      setRegenerating(false);
    }
  };

  const handleDeleteReport = async () => {
    if (!window.confirm('Delete this career report? This action cannot be undone.')) {
      return;
    }

    try {
      setDeleting(true);
      console.log('🗑️ Deleting career report:', report.id);
      
      const { getFirestore, doc, deleteDoc } = await import('firebase/firestore');
      const db = getFirestore();
      await deleteDoc(doc(db, 'careerReports', report.id));
      
      console.log('✅ Report deleted successfully');
      alert('✅ Career report deleted successfully');
      
      if (onDelete) {
        onDelete(report.id);
      }
      
      if (onRefresh) {
        onRefresh();
      }
      
    } catch (error) {
      console.error('❌ Deletion failed:', error);
      alert(`❌ Deletion failed: ${error.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const isProcessing = ['processing', 'analyzing', 'payment_complete'].includes(report.status);
  const isFailed = report.status === 'failed';
  const isComplete = (report.status === 'completed' || report.status === 'analyzed') && report.pdfUrl;
  const canRegenerate = isComplete && !report.regenerated;

  return (
    <Card
      sx={{
        backgroundColor: '#171717',
        border: '1px solid #262626',
        borderRadius: '12px',
        transition: 'all 0.3s ease',
        '&:hover': {
          borderColor: '#00FFF5',
          boxShadow: '0 4px 20px rgba(0,255,245,0.1)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            {/* Checkbox for bulk selection */}
            {showCheckbox && (
              <Checkbox
                checked={selected}
                onChange={(e) => onSelect && onSelect(report.id, e.target.checked)}
                sx={{
                  color: '#525252',
                  '&.Mui-checked': { color: '#00FFF5' },
                  padding: '4px',
                  mt: 0.5
                }}
              />
            )}
            
            <Box>
              <Typography
                variant="h6"
                sx={{
                  color: '#fafafa',
                  fontFamily: 'monospace',
                  fontWeight: 600,
                  mb: 0.5
                }}
              >
                {report.currentRole || 'Career Transition'} → {report.targetRole || 'Target Role'}
              </Typography>
              <Typography variant="body2" sx={{ color: '#737373', fontFamily: 'monospace' }}>
                Created: {report.createdAt ? new Date(report.createdAt.toDate()).toLocaleDateString() : 'N/A'}
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Plan Status Badge */}
            {(() => {
              const badge = getPlanStatusBadge();
              return (
                <Chip
                  label={badge.label}
                  size="small"
                  sx={{
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: '0.65rem',
                    color: badge.color,
                    bgcolor: badge.bgcolor,
                    border: `1px solid ${badge.color}`
                  }}
                />
              );
            })()}
            
            {/* Processing Status Badge */}
            <Chip
              label={getStatusText()}
              color={getStatusColor(report.status)}
              size="small"
              sx={{ fontFamily: 'monospace', fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Progress bar for processing reports */}
        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress
              sx={{
                backgroundColor: '#262626',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: '#00FFF5'
                }
              }}
            />
            <Typography
              variant="caption"
              sx={{ color: '#737373', fontFamily: 'monospace', display: 'block', mt: 1 }}
            >
              Analyzing job market data and generating your personalized career plan...
            </Typography>
          </Box>
        )}

        {/* Qualification Score (if available) - use gapAnalysis.qualificationPercentage for consistency with Profile */}
        {(report.gapAnalysis?.qualificationPercentage !== undefined || report.readinessScore !== undefined) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 0.5 }}>
              Qualification Score
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography
                variant="h4"
                sx={{ color: '#00FFF5', fontFamily: 'monospace', fontWeight: 300 }}
              >
                {report.gapAnalysis?.qualificationPercentage ?? report.readinessScore}%
              </Typography>
            </Box>
          </Box>
        )}

        <Divider sx={{ my: 2, borderColor: '#262626' }} />

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {isComplete && (
            <>
              <Button
                variant="contained"
                startIcon={<ViewIcon />}
                onClick={handleViewReport}
                sx={{
                  backgroundColor: '#00FFF5',
                  color: '#0a0a0a',
                  fontFamily: 'monospace',
                  '&:hover': {
                    backgroundColor: '#00CCC5'
                  }
                }}
              >
                View Report
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadReport}
                sx={{
                  color: '#fafafa',
                  borderColor: '#262626',
                  fontFamily: 'monospace',
                  '&:hover': {
                    borderColor: '#00FFF5',
                    color: '#00FFF5'
                  }
                }}
              >
                Download
              </Button>
            </>
          )}

          {isFailed && (
            <Button
              variant="contained"
              startIcon={retrying ? null : <RetryIcon />}
              onClick={handleRetryAnalysis}
              disabled={retrying}
              sx={{
                backgroundColor: '#9D00FF',
                color: '#fafafa',
                fontFamily: 'monospace',
                '&:hover': {
                  backgroundColor: '#8000CC'
                }
              }}
            >
              {retrying ? 'Retrying...' : 'Retry Analysis'}
            </Button>
          )}

          {canRegenerate && (
            <IconButton
              onClick={handleRegenerateReport}
              disabled={regenerating}
              sx={{
                color: '#9D00FF',
                '&:hover': {
                  backgroundColor: 'rgba(157, 0, 255, 0.1)'
                }
              }}
              title="Regenerate Report"
            >
              <RegenerateIcon />
            </IconButton>
          )}

          {/* Set Active Button - only show if not already active */}
          {isComplete && !report.isActive && (
            <Button
              variant="outlined"
              startIcon={settingActive ? null : <InactiveIcon />}
              onClick={handleSetActive}
              disabled={settingActive}
              size="small"
              sx={{
                color: '#fbbf24',
                borderColor: '#fbbf24',
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                '&:hover': {
                  borderColor: '#fbbf24',
                  backgroundColor: 'rgba(251, 191, 36, 0.1)'
                }
              }}
            >
              {settingActive ? 'Setting...' : 'Set Active'}
            </Button>
          )}

          <IconButton
            onClick={handleDeleteReport}
            disabled={deleting}
            sx={{
              color: '#ef4444',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.1)'
              }
            }}
            title="Delete Report"
          >
            <DeleteIcon />
          </IconButton>
        </Box>

        {/* Error message (if failed) */}
        {isFailed && report.error && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: '#0a0a0a',
              border: '1px solid #ef4444',
              borderRadius: '8px'
            }}
          >
            <Typography variant="body2" sx={{ color: '#ef4444', fontFamily: 'monospace' }}>
              Error: {report.error}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CareerReportCard;

